/**
 * Price Action Dashboard Overlay
 * Toggle support, arrow badges, status highlighting.
 * CSS prefix: pa-
 */
import React from "react";
import { PriceActionDashboard } from "../../indicators/built-in/price-action";
import "./PriceActionOverlay.scss";

interface PriceActionOverlayProps {
  dashboard: PriceActionDashboard | null;
  symbol: string;
  visible: boolean;
  showDashboard?: boolean;
  onToggleDashboard?: () => void;
  arrowBadges?: { x: number; y: number; direction: "long" | "short" }[];
}

const PriceActionOverlay: React.FC<PriceActionOverlayProps> = ({
  dashboard, symbol, visible,
  showDashboard = true, onToggleDashboard, arrowBadges = [],
}) => {
  if (!visible || !dashboard) return null;

  const {
    signal, trend, entry, stopLoss, target1, target2,
    currentPrice, livePL, bookedPL, status, tp1Hit, tp2Hit, slHit, tslHit,
  } = dashboard;

  const isLong = trend === "LONG";
  const isShort = trend === "SHORT";
  const themeClass = isLong ? "pa-long" : isShort ? "pa-short" : "pa-neutral";
  const fmt = (v: number | null) => (v !== null ? v.toFixed(2) : "—");
  const fmtPL = (v: number) => (v >= 0 ? `+${v.toFixed(2)}` : v.toFixed(2));

  const signalLabel = isShort ? "Sell" : isLong ? "Buy" : "—";

  const statusText = status === "Trailing SL Hit" ? "TSL Hit" : status;

  // Status CSS class
  const statusClass =
    status === "Open" ? "pa-status-open" :
    status === "TGT-1 Hit" ? "pa-status-tgt" :
    status === "TGT-2 Hit" ? "pa-status-tgt" :
    status === "SL Hit" ? "pa-status-sl" :
    status === "Trailing SL Hit" ? "pa-status-tsl" : "";

  const rows = [
    { label: signalLabel, value: fmt(entry), hit: false, isSig: true },
    { label: "TGT - 1", value: fmt(target1), hit: tp1Hit, isSig: false },
    { label: "TGT - 2", value: fmt(target2), hit: tp2Hit, isSig: false },
    { label: "SL", value: fmt(stopLoss), hit: slHit, isSig: false },
  ];

  return (
    <>
      {/* Arrow Badges */}
      {arrowBadges.map((badge, i) => (
        <div
          key={i}
          className={`pa-arrow-badge pa-arrow-badge--${badge.direction}`}
          style={{
            left: badge.x,
            top: badge.direction === "long" ? badge.y + 30 : badge.y - 44,
          }}
        >
          {badge.direction === "long" ? "Buy" : "Sell"}
        </div>
      ))}

      <div className={`pa-overlay ${themeClass}${showDashboard ? "" : " pa-overlay--collapsed"}`}>
        {showDashboard && (
          <>
            {/* Header: Symbol row + Price row + Toggle */}
            <div className="pa-header">
              <div className="pa-header-row">
                <span className="pa-symbol">{symbol}</span>
                <button
                  className="pa-toggle-btn"
                  onClick={onToggleDashboard}
                  title="Hide dashboard"
                  aria-label="Hide dashboard"
                >
                  <span className="pa-toggle-icon">&#x25B2;</span>
                </button>
              </div>
              <span className="pa-cmp">{currentPrice.toFixed(2)}</span>
            </div>

            {/* Signal rows */}
            <div className="pa-rows">
              {rows.map((r, i) => (
                <div key={i} className={`pa-row ${r.hit ? "pa-hit-row" : ""}`}>
                  <span className={`pa-row-label ${r.isSig ? "pa-sig-label" : ""}`}>{r.label}</span>
                  <span className="pa-row-value">{r.value}</span>
                </div>
              ))}
            </div>

            {/* Pill boxes: Status, Live PL, Booked P&L */}
            <div className="pa-pills">
              <div className="pa-pill">
                <span className="pa-pill-label">Status</span>
                <span className={`pa-pill-value ${statusClass}`}>{statusText}</span>
              </div>
              <div className="pa-pill">
                <span className="pa-pill-label">Live PL</span>
                <span className={`pa-pill-value ${livePL >= 0 ? "pa-profit" : "pa-loss"}`}>{fmtPL(livePL)}</span>
              </div>
              <div className="pa-pill">
                <span className="pa-pill-label">Booked P&amp;L</span>
                <span className="pa-pill-value">{bookedPL.toFixed(2)}</span>
              </div>
            </div>

            {/* Brand */}
            <div className="pa-brand">Price Action</div>
          </>
        )}

        {!showDashboard && (
          <>
            <div className="pa-collapsed-title">Price Action</div>
            <button
              className="pa-toggle-btn"
              onClick={onToggleDashboard}
              title="Show dashboard"
              aria-label="Show dashboard"
            >
              <span className="pa-toggle-icon">&#x25BC;</span>
            </button>
          </>
        )}
      </div>
    </>
  );
};

export default PriceActionOverlay;
